import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, Award, Snowflake } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1761330440311-16e160cad236?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBhaXIlMjBjb25kaXRpb25lciUyMGludGVyaW9yJTIwbGl2aW5nJTIwcm9vbXxlbnwwfHx8fDE3OTAxOTMwNDF8MA&ixlib=rb-4.1.0";

const MaskedLine = ({ children, i }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ delay: 0.25 + i * 0.14, duration: 0.95, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const STATS = [
  { value: "5+", label: "Años de experiencia" },
  { value: "2", label: "Años garantía instalación" },
  { value: "3", label: "Años garantía máquina" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section id="inicio" ref={ref} data-testid="hero-section" className="relative overflow-hidden bg-ocean-50/60">
      <div className="pointer-events-none absolute -left-40 top-24 h-96 w-96 rounded-full bg-ocean-500/10 blur-3xl" />
      <div className="pointer-events-none absolute -right-32 bottom-0 h-80 w-80 rounded-full bg-ember/10 blur-3xl" />

      <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 pb-20 pt-36 sm:px-6 lg:grid-cols-12 lg:px-12 lg:pb-28 lg:pt-44">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="inline-flex items-center gap-2 rounded-full border border-ocean-600/20 bg-white px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-ocean-700"
            data-testid="hero-eyebrow"
          >
            <Snowflake className="h-3.5 w-3.5" />
            Empresa instaladora · Fundada en 2021
          </motion.div>

          <h1 className="mt-7 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-navy sm:text-5xl lg:text-6xl text-balance">
            <MaskedLine i={0}>El clima perfecto,</MaskedLine>
            <MaskedLine i={1}>
              instalado con <span className="text-ocean-600">precisión.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg"
            data-testid="hero-subheadline"
          >
            En Clima Oliver llevamos más de 5 años diseñando e instalando sistemas de
            aire acondicionado residenciales y comerciales en la provincia de Tarragona
            y Barcelona, con un estándar de excelencia formal: 2 años de garantía en la
            instalación y 3 años de garantía en la máquina, siempre por escrito.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/presupuesto"
              data-testid="hero-create-budget-btn"
              className="group inline-flex items-center gap-2.5 rounded-full bg-ember px-7 py-3.5 text-sm font-semibold text-white shadow-xl shadow-ember/30 transition-all duration-300 hover:-translate-y-0.5 hover:bg-ember-700"
            >
              Crear mi presupuesto ahora
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <a
              href="#nosotros"
              data-testid="hero-about-us-btn"
              className="inline-flex items-center gap-2 rounded-full border border-navy/15 bg-white px-7 py-3.5 text-sm font-semibold text-navy transition-all duration-300 hover:border-ocean-600/40 hover:text-ocean-700"
            >
              Conocer Clima Oliver
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.05 }}
            className="mt-12 grid grid-cols-3 gap-6 border-t border-slate-200 pt-8"
            data-testid="hero-stats"
          >
            {STATS.map((s) => (
              <div key={s.label}>
                <p className="font-display text-3xl font-extrabold text-navy sm:text-4xl">
                  {s.value}
                </p>
                <p className="mt-1 text-xs font-medium uppercase tracking-wider text-slate-500">
                  {s.label}
                </p>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative overflow-hidden rounded-[2rem] shadow-2xl shadow-navy/20 ring-1 ring-slate-200"
          >
            <motion.img
              src={HERO_IMG}
              alt="Salón moderno climatizado con aire acondicionado instalado por Clima Oliver"
              style={{ y: imgY, scale: imgScale }}
              className="aspect-[4/5] w-full object-cover"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy/30 via-transparent to-transparent" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1 }}
            className="absolute -bottom-6 -left-4 animate-float-slow rounded-2xl border border-slate-200 bg-white/95 p-5 shadow-xl backdrop-blur sm:-left-10"
            data-testid="hero-floating-card"
          >
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-ocean-50 text-ocean-600">
                <ShieldCheck className="h-5 w-5" />
              </span>
              <div>
                <p className="font-display text-sm font-bold text-navy">Garantía certificada</p>
                <p className="text-xs text-slate-500">Instalación 2 años · Máquina 3 años</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: -18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.25 }}
            className="absolute -top-5 right-4 rounded-2xl border border-slate-200 bg-white/95 px-5 py-3 shadow-lg backdrop-blur"
            data-testid="hero-since-badge"
          >
            <div className="flex items-center gap-2">
              <Award className="h-4 w-4 text-ember" />
              <p className="font-display text-xs font-bold uppercase tracking-widest text-navy">
                Desde 2021
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
